import { useCallback } from "react";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import {
  DownloadPopupBlockedError,
  downloadInNewTab,
} from "@/utils/downloadInNewTab";
import { getKanpoNoticePdfUrl } from "../api";
import { useCorporateKanpoNotices } from "../hooks/useCorporateKanpoNotices";
import type { KanpoNoticeItem } from "../types";
import { KanpoTab } from "./KanpoTab";

/**
 * 法人マスタの詳細にある「官報」タブ。公告を取得して KanpoTab に渡す。
 * PDFのボタンを押したときに、署名付きURLを発行し、公告のあるページ（#page=N）から新しいタブで開く
 * @param corporateNumber 法人番号（13桁）
 */
export const KanpoNoticesTab: React.FC<{ corporateNumber: string }> = ({
  corporateNumber,
}) => {
  const { apiClient } = useApiClient();
  const { data, isLoading, error, mutate } =
    useCorporateKanpoNotices(corporateNumber);

  const openPdf = useCallback(
    async (item: KanpoNoticeItem): Promise<void> => {
      try {
        // 共通部品が、押下直後に空のタブを開き（ポップアップのブロック対策）、URLの発行後にそのタブを移す
        await downloadInNewTab(
          async () =>
            `${await getKanpoNoticePdfUrl(
              apiClient,
              corporateNumber,
              item.id
            )}#page=${item.pageNumber}`
        );
      } catch (e: unknown) {
        if (e instanceof DownloadPopupBlockedError) {
          toast.error(
            "ポップアップがブロックされました。許可してから再試行してください。"
          );
        } else if (isAxiosError(e) && e.response?.status === 404) {
          toast.error("官報のPDFが見つかりませんでした。");
        } else {
          // 5xx とネットワーク断だけを Sentry へ送る（想定内の 4xx は送らない）
          if (
            !isAxiosError(e) ||
            e.response == null ||
            e.response.status >= 500
          ) {
            Sentry.captureException(e);
          }
          toast.error(
            "官報のPDFを開けませんでした。時間をおいて再度お試しください"
          );
        }
      }
    },
    [apiClient, corporateNumber]
  );

  return (
    <KanpoTab
      notices={data?.list ?? []}
      isLoading={isLoading}
      hasError={Boolean(error)}
      onRetry={() => {
        void mutate();
      }}
      onOpenPdf={(item) => {
        void openPdf(item);
      }}
    />
  );
};
